<template>
  <div class="mapContainer">
    <b-modal ref="lupcMessageModal" id="lupcMessageModal"
           v-if="isLupcTownChanged && isMapSection && !mfsAdmin"
           :visible="!loading && townExtent"
           centered hide-header no-close-on-backdrop no-stacking >
           <p class = "lupc-modal-title"><strong>LUPC AREA DETECTED</strong></p>
      <p class="lupc-modal-text">
        You chose an area in an <i>unorganized territory</i>. The <b>MFS</b> has rules called <b>Statewide Standards</b>
         for harvesting timber and doing related work near waterbodies or nonforested wetlands.
          This map layer is on by default, but it’s only a guide. Actual ground conditions determine
          where and how the rules apply.<br/>
         Depending on where your work is, you may also need an additional permit from the <b>MFS</b> or the
        <b>Maine Land Use Planning Commission (LUPC)</b>. To check if your area is inside an LUPC zone, click
         “Continue and Turn LUPC Layer On” and use the map to help draw your activity.
          You can turn layers on and off by using the "Layers" icon (<img src="../../assets/layerButton_s.png">) on the map.<br/>
        If your drawn activity requires a permit, you will be notified on the summary page.
      </p>
      <div class="d-flex flex-column btn-wrapper">
        <b-button variant="primary" pill class="mb-3 mb-lg-4 lupc-modal-btn" @click="closeOnLupcMessageModal" aria-label="Continue and turn on LUPC layer">
          Continue and Turn LUPC Layer On
        </b-button>
        <b-button variant="primary" pill class="lupc-modal-btn" @click="closeLupcMessageModal" aria-label="Continue with LUPC Layer Off">
          Continue with LUPC Layer Off
        </b-button>
      </div>
        <div class = "back-to-overview-wrapper">
          <div @click="closeBackLupcMessageModal" class = "back-to-overview">Back to Overview</div>
        </div>
      <div slot="modal-footer" class="footer-hide">
      </div>
    </b-modal>
    <Map ref="map" v-if="!loading && (activityExtent || townExtent)" :key="mapKey" mapId="form-map"
      :extent="activityExtent || townExtent" :class="drawerOpen ? '' : 'fullHeight'"
      @activityClicked="activityMapClick" />
    <!-- ABOUT THIS MAP BTN & MODAL -->
    <b-button pill variant="primary" class="about-map" v-if="isExtraSmall" @click="openAboutMap" id="aboutMapBtn" aria-label="About this Map">
      <font-awesome-icon icon="question" size="lg" />
    </b-button>
    <b-modal ref="aboutModal" id="aboutModal" centered hide-header-close size="md" title="About this Map">
      <p>
        You must map where each harvest, road construction, and water crossing
        activity will be located for this operation. You must map all activities,
        and can access them in a list at the bottom of the map.
      </p>
      <div class="d-flex flex-column px-2 justify-content-center">
        <div class="d-flex flex-row align-items-center py-2">
          <div class="btn btn-primary rounded-pill about-icon">
            <font-awesome-icon icon="question" size="lg" />
          </div>
          <div class="pl-2">About this Map</div>
        </div>
        <div class="d-flex flex-row align-items-center py-2">
          <div class="btn btn-primary rounded-pill about-icon">
            <font-awesome-icon icon="layer-group" size="lg" />
          </div>
          <div class="pl-2">Basemaps and Reference Layers</div>
        </div>
        <div class="d-flex flex-row align-items-center py-2">
          <div class="btn btn-primary rounded-pill about-icon">
            <font-awesome-icon icon="expand" size="lg" />
          </div>
          <div class="pl-2">Reset Map Extent</div>
        </div>
        <div class="d-flex flex-row align-items-center py-2">
          <div class="btn btn-primary rounded-pill about-icon">
            <font-awesome-icon icon="plus" size="lg" />
          </div>
          <div class="pl-2">Add Activity</div>
        </div>
      </div>
      <div>
        <div class="pl-2 mt-2">Attributions</div>
        <ul  class="font-xs">
          <li v-for="layer in attributions" :key="layer.id">
            {{ layer.attribution }}
          </li>
        </ul>
      </div>
      <div slot="modal-footer" class="d-flex w-100 justify-content-center">
        <b-button pill variant="primary" @click="closeAboutMap" aria-label="Close About this Map Modal">
          OK
        </b-button>
      </div>
    </b-modal>
    <!-- Initial Help Text for Desktop -->
    <div v-if="initialHelp && !workflowActive && !isExtraSmall"
      class="map-help-control d-flex bg-dark ol-unselectable p-2 map-help-init-control">
      <b-button v-b-toggle.mapHelpCollapse variant="primary" pill class="map-help-toggle" aria-label="Initial Help">
        ?
      </b-button>
      <b-collapse visible id="mapHelpCollapse" class="map-help-content ml-2 border-left border-white pl-2">
        <span class="text-white font-sm">{{ getInitHelpTitle() }}</span>
        <p class="text-white font-sm mb-1">{{ getInitHelpText() }}</p>
      </b-collapse>
    </div>
    <!-- Workflow Help Text for Desktop -->
    <div v-if="workflowActive && showWorkflowHelp && !isExtraSmall"
      class="map-help-control d-flex bg-dark ol-unselectable p-2">
      <b-button v-b-toggle.mapHelpCollapse variant="primary" pill class="map-help-toggle" aria-label="Workflow Help">
        ?
      </b-button>
      <b-collapse visible id="mapHelpCollapse" class="map-help-content ml-2 border-left border-white pl-2">
        <span class="text-white font-sm">{{ getWorkflowTitle() }}</span>
        <p class="text-white font-sm mb-1">{{ getWorkflowText() }}</p>
      </b-collapse>
    </div>
    <!-- MAP DRAWER -->
    <b-container id="mapDrawer" fluid>
      <!-- CANCEL CONFIRMATION MODAL FOR MOBILE -->
      <b-modal ref="cancelConfirmModal" id="cancelConfirmModal" centered hide-header-close size="md"
      :title="`Cancel ${activityTypeToCancel} ${activityIdToCancel}`">
          <p>Are you sure you want to cancel the activity? </p>
        <div slot="modal-footer" class="d-flex full-width">
          <b-button
            pill
            variant="danger"
            class="mr-3"
            @click="closeConfirmCancel(false)" aria-label="No, do not cancel activity">
            No
          </b-button>
          <b-button
            pill
            variant="primary"
            @click="closeConfirmCancel(true)" aria-label="Yes, cancel activity">
            Yes
          </b-button>
        </div>
      </b-modal>
      <!-- Save/Cancel Activity Workflow (Mobile) -->
      <section v-if="workflowActive && isExtraSmall" class="save-activity-section p-2 w-100">
        <section v-if="showWorkflowHelp" class="workflow-help d-flex flex-column w-100">
          <header class="d-flex flex-row align-items-center">
            <div class="w-100">{{ getWorkflowTitle() }}</div>
            <b-button variant="link" @click="hideWorkflowHelp" aria-label="Hide workflow help">
              <font-awesome-icon icon="times" />
            </b-button>
          </header>
          <p>{{ getWorkflowText() }}</p>
        </section>
        <b-alert v-if="currentActivityError" :show="!activityErrorDismissed" @dismissed="activityErrorDismissed = true"
          dismissible variant="danger" class="font-sm">
          {{ currentActivityError }}
        </b-alert>
        <div class="d-flex flex-column w-100">
          <b-button variant="primary" pill class="mb-2" size="sm" :disabled="drawing || submitting" @click="save(null)" aria-label="Save Activity">
            Save
          </b-button>
          <b-button variant="danger" pill size="sm"  @click="openConfirmCancel(activities)" aria-label="Cancel Activity">
            Cancel
          </b-button>
        </div>
      </section>
      <!-- Drawer nav/pull tab -->
      <nav class="d-flex justify-content-between align-items-center">
        <div>
          <h5 class="d-none d-lg-block text-secondary font-weight-bold m-0">
            Activities ( {{ activities.length }} )
          </h5>
          <h6 class="d-block d-lg-none text-white font-weight-bold m-0">
            Activities ( {{ activities.length }} )
          </h6>
        </div>
        <div class="d-flex align-items-center">
          <!-- Add Activity Btn and Workflow Modals -->
          <b-button v-if="!workflowActive" variant="primary" pill class="add-activity-btn" size="sm"
            :disabled="mfsAdmin || drawing || editing" @click="openAddActivitySelector" aria-label="Add Activity">
            <font-awesome-icon v-if="isExtraSmall" icon="plus" size="sm" />
            <span v-else>
              <font-awesome-icon icon="plus" size="sm" />&nbsp;&nbsp;Add Activity
            </span>
          </b-button>
          <!-- Add Activity Mode Modal -->
          <b-modal ref="selectModeModal" id="selectModeModal" centered hide-header-close no-close-on-backdrop
            no-stacking size="sm" title="How would you like to add your activities?">
            <div class="d-flex flex-column">
              <b-button variant="primary" pill class="mb-3 mb-lg-4" @click="beginDrawWorkflow" aria-label="Draw on the Map">
                Draw on the Map
              </b-button>
              <b-button variant="primary" pill @click="beginImportWorkflow" aria-label="Upload Shapefile">
                Upload Shapefile
              </b-button>
            </div>
            <div slot="modal-footer" class="d-flex full-width">
              <b-button pill variant="danger" @click="closeAddActivityMode" aria-label="Cancel adding activity mode">
                Cancel
              </b-button>
            </div>
          </b-modal>
          <!-- Add Activity Modal -->
          <b-modal ref="addActivityModal" id="addActivityModal" centered hide-header-close no-close-on-backdrop
            no-stacking size="sm" title="Which type of activity do you want to add?">
            <div class="d-flex flex-column">
              <b-button variant="primary" pill class="mb-3 mb-lg-4" @click="addActivity(activityTypes.harvest)">
                Harvest
              </b-button>
              <b-button variant="primary" pill class="mb-3 mb-lg-4"
                @click="addActivity(activityTypes.roadConstruction)">
                Road Construction
              </b-button>
              <b-button variant="primary" pill @click="addActivity(activityTypes.waterCrossing)" aria-label="Add Water Crossing Activity">
                Water Crossing
              </b-button>
            </div>
            <div slot="modal-footer" class="d-flex full-width">
              <b-button pill variant="danger" @click="closeAddActivity" aria-label="Cancel adding activity">
                Cancel
              </b-button>
            </div>
          </b-modal>
          <!-- Water Crossing Details Modal -->
          <ValidationObserver v-slot="{ pristine, invalid }" slim>
            <b-modal ref="waterCrossingDetailsModal" id="waterCrossingDetailsModal" centered hide-header-close
              no-close-on-backdrop no-stacking size="sm" title="Enter Additional Details">
              <div class="mb-4">
                <h6><strong>Activity:</strong> Water Crossing</h6>
              </div>
              <ValidationProvider name="Permanence" rules="required" v-slot="{ errors, classes }" slim>
                <b-form-group v-if="currentActivity && waterCrossingWorkflow" label-for="structurePermanence">
                  <template v-slot:label>
                    <span class="req-ind">*</span>Permanence
                  </template>
                  <select id="structurePermanence" name="structurePermanence"
                    v-model="currentActivity.structurePermanence" class="form-control" :class="classes"
                    :aria-describedby="$errorsIdString(errors, 'structurePermanence')">
                    <option :value="null" disabled>Pick one</option>
                    <option :value="false">Temporary</option>
                    <option :value="true">Permanent</option>
                  </select>
                  <InputErrors :errors="errors" errorsIdPrefix="structurePermanence" />
                </b-form-group>
              </ValidationProvider>
              <ValidationProvider name="Structure Type" rules="required" v-slot="{ errors, classes }" slim>
                <b-form-group v-if="currentActivity && waterCrossingWorkflow" label-for="structureType">
                  <template v-slot:label>
                    <span class="req-ind">*</span>Structure Type
                  </template>
                  <select id="structureType" name="structureType" v-model="currentActivity.structureTypeId"
                    class="form-control" :class="classes" :aria-describedby="$errorsIdString(errors, 'structureType')">
                    <option :value="null" disabled>Pick one</option>
                    <option v-for="option in structureTypes" :key="option.id" :value="option.id">
                      {{ option.name }}
                    </option>
                  </select>
                  <InputErrors :errors="errors" errorsIdPrefix="structureType" />
                </b-form-group>
              </ValidationProvider>
              <div slot="modal-footer" class="d-flex full-width">
                <b-button pill variant="danger" class="mr-0 mr-lg-3" @click="cancelAddActivity" aria-label="Cancel adding activity">
                  Cancel
                </b-button>
                <b-button pill variant="primary" :disabled="pristine || invalid" @click="continueWaterCrossingWorkflow" aria-label="Continue Water Crossing Workflow">
                  Continue
                </b-button>
              </div>
            </b-modal>
          </ValidationObserver>
          <!-- Road Construction Details Modal -->
          <ValidationObserver v-slot="{ pristine, invalid }" slim>
            <b-modal ref="roadDetailsModal" id="roadDetailsModal" centered hide-header-close no-close-on-backdrop
              no-stacking size="sm" title="Enter Additional Details">
              <div class="mb-4">
                <h6><strong>Activity:</strong> Road Construction</h6>
              </div>
              <ValidationProvider name="Permanence" rules="required" v-slot="{ errors, classes }" slim>
                <b-form-group v-if="currentActivity && roadConstructionWorkflow" label-for="structurePermanence">
                  <template v-slot:label>
                    <span class="req-ind">*</span>Permanence
                  </template>
                  <select id="structurePermanence" name="structurePermanence"
                    v-model="currentActivity.structurePermanence" class="form-control" :class="classes"
                    :aria-describedby="$errorsIdString(errors, 'structurePermanence')">
                    <option :value="null" disabled>Pick one</option>
                    <option :value="false">Temporary</option>
                    <option :value="true">Permanent</option>
                  </select>
                  <InputErrors :errors="errors" errorsIdPrefix="structurePermanence" />
                </b-form-group>
              </ValidationProvider>
              <div slot="modal-footer" class="d-flex full-width">
                <b-button pill variant="danger" class="mr-0 mr-lg-3" @click="cancelAddActivity" aria-label="Cancel adding activity">
                  Cancel
                </b-button>
                <b-button pill variant="primary" :disabled="pristine || invalid"
                  @click="continueRoadConstructionWorkflow" aria-label="Continue Road Construction Workflow">
                  Continue
                </b-button>
              </div>
            </b-modal>
          </ValidationObserver>
          <!-- Activity Created Successfully Modal -->
          <b-modal ref="activityCreatedModal" id="activityCreatedModal" centered hide-header-close no-close-on-backdrop
            no-stacking size="sm" title="Activity Created">
            <p>
              Your Activity has been added. You can add another Activity or move on
              to the next step.
            </p>
            <p>
              Activities can be viewed at the bottom of the screen.
              <span v-if="!isExtraSmall">Click</span>
              <span v-else>Tap</span> the arrow to expand the list.
              You can delete an Activity if it is incorrect.
            </p>
            <div slot="modal-footer" class="d-flex full-width">
              <b-button pill variant="primary" @click="closeActivityCreated" aria-label="Close Activity Created Modal">
                OK
              </b-button>
            </div>
          </b-modal>
          <!-- Upload Activity Modal -->
          <b-modal ref="uploadActivityModal" id="uploadActivityModal" centered hide-header-close no-close-on-backdrop
            no-stacking size="lg" title="Upload Activities">
            <div class="d-flex flex-column">
              <div class="font-weight-bold">
                IF YOU DO NOT KNOW WHAT A SHAPEFILE IS, YOU SHOULD NOT SELECT THIS METHOD OF MAPPING AND CLICK CANCEL
                BELOW.
              </div>
              <div class="mt-3">
                Upload a zip file containing the shapefile with the area of your activity. Each feature in the file
                will appear as a separate item in the "Activities" panel.
              </div>
              <div class="mt-3">
                Zip file <strong>must</strong> include, at minimum, the following extensions: shp, dbf, prj, shx. <strong>Features uploaded without a .prj file might not be re-projected or placed on the map in your anticipated location.</strong>
              </div>
              <div class="mt-3">
                Supported geometry types are: Point, LineString, and Polygon.
              </div>
              <div class="mt-3">
                You cannot upload more than {{ localeString(importMaxShapeCount) }} shapes in a shapefile. If there are more than {{ localeString(importMaxShapeCount) }}, please upload another shapefile.
              </div>
              <div class="mt-3">
                A shape cannot exceed {{ localeString(importMaxVertsPerShape) }} vertices. Please simplify the shape before uploading if it exceeds {{ localeString(importMaxVertsPerShape) }} vertices.
              </div>
              <div class="mt-3 mb-3">
                All options must be set per activity before saving. Do you wish to continue?
              </div>
              <input type="file" ref="fileupload" style="display: none" accept="application/zip"
                @change="loadShapeFile" />
              <b-button variant="primary" pill @click="$refs.fileupload.click()" aria-label="Choose Shapefile">
                Choose Shapefile
              </b-button>
            </div>
            <div slot="modal-footer" class="d-flex full-width">
              <b-button pill variant="danger" @click="closeUploadActivity" aria-label="Cancel Upload Activity">
                Cancel
              </b-button>
            </div>
          </b-modal>
           <!-- Upload Activity Success Modal -->
           <b-modal ref="uploadActivitySuccessModal" id="uploadActivitySuccessModal" centered hide-header-close
            no-close-on-backdrop no-stacking size="md" title="Activities Uploaded">
            <div class="d-flex flex-column">
              <div>
                Your Activity has been added. Please review all of them in the table below and Save or Cancel each. You can add another Activity or move on to the next step.
              </div>
            </div>
            <div slot="modal-footer" class="d-flex full-width">
              <b-button pill variant="primary" @click="closeUploadActivitySuccess" aria-label="Close Upload Activity Success Modal">
                OK
              </b-button>
            </div>
          </b-modal>
          <!-- Upload Activity Error Modal -->
          <b-modal ref="uploadActivityErrorModal" id="uploadActivityErrorModal" centered hide-header-close
            no-close-on-backdrop no-stacking size="md" title="Upload Activities Error">
            <div class="d-flex flex-column">
              <div>
                {{ uploadError }}
              </div>
            </div>
            <div slot="modal-footer" class="d-flex full-width">
              <b-button pill variant="primary" @click="closeUploadActivityError" aria-label="Close Upload Activity Error Modal">
                OK
              </b-button>
            </div>
          </b-modal>
          <!-- Drawer Toggle -->
          <a v-b-toggle="'collapse-drawer'" class="collapse-icon c-pointer">
            <font-awesome-icon class="when-opened" icon='caret-down' :size="isExtraSmall ? 'lg' : '2x'" />
            <font-awesome-icon class="when-closed fa-w-10" icon='caret-right' :size="isExtraSmall ? 'lg' : '2x'" />
          </a>
        </div>
      </nav>
      <!-- COLLAPSIBLE ACTIVITY LIST -->
      <b-collapse id="collapse-drawer" v-model="drawerOpen" :visible="!this.isExtraSmall" @hidden="resizeMap"
        @shown="resizeMap">
        <!-- MOBILE LIST VIEW -->
        <div v-if="isExtraSmall" class="activity-list-sm pb-2">
          <!-- Activity List -->
          <ul v-if="activities.length > 0" class="py-2 w-100 h-100 d-flex flex-column">
            <li class="activity my-2 mx-3 p-2" v-for="activity in activities" :key="activity.activityId"
              :id="`act-row-${activity.activityId}`">
              <!-- Activity Details -->
              <div class="activity-details flex-column w-100" @click="zoomToActivity(activity)">
                <div class="d-flex align-items-center">
                  <b-badge pill variant="primary">{{ activity.activityId }}</b-badge>
                  &nbsp; {{ activity.type.name }}
                </div>
                <div v-if="hasCalculatedSize(activity)">
                  {{ activity.size }}
                </div>
                <div v-if="hasStructurePermanence(activity)">
                  <strong>Permanence:</strong>&nbsp;{{ permanenceToString(activity) }}
                </div>
                <div v-if="hasStructureTypeId(activity)">
                  <strong>Structure Type:</strong>
                  &nbsp;{{ getStructureTypeName(activity.structureTypeId) }}
                </div>
              </div>
              <!-- Delete Activity -->
              <div v-if="activity.id" class="activity-btns flex-column">
                <b-button variant="danger" :disabled="mfsAdmin || submitting" pill
                  v-b-modal="`deleteConfirmation-activity-${activity.activityId}`"
                  @deleteConfirmed="deleteActivity(activity)" aria-label="Delete Activity">
                  <font-awesome-icon icon="trash-alt" />
                </b-button>
                <DeleteModal :modalId="`deleteConfirmation-activity-${activity.activityId}`"
                  :deleteDisplayName="`Delete ${activity.type.name} ${activity.activityId}`" deletePrompt="Are you sure you want to permanently delete this mapped
                  activity?" @deleteConfirmed="deleteActivity(activity)">
                </DeleteModal>
              </div>
            </li>
            <li class="trailer">&nbsp;</li>
          </ul>
          <!-- Empty Activity List -->
          <ul v-else class="py-2 w-100 h-100 d-flex flex-column">
            <li class="no-activity h-100 my-2 mx-3 p-2 align-items-center">
              <div class="flex-column w-100">
                No activities have been mapped yet. Please use the "+" button to map an activity.
              </div>
            </li>
          </ul>
        </div>
        <!-- LARGE SCREEN TABLE VIEW -->
        <div v-else>
          <b-row class="bg-primary text-white p-2 pr-4">
            <b-col cols="3">
              <b-form-row>
                <b-col cols="5">
                  Activity ID
                </b-col>
                <b-col cols="7">
                  Activity Type
                </b-col>
              </b-form-row>
            </b-col>
            <b-col cols="5">
              <b-form-row>
                <b-col cols="4">
                  Calculated Size
                </b-col>
                <b-col cols="4">
                  Permanent?
                </b-col>
                <b-col cols="4">
                  Structure Type
                </b-col>
              </b-form-row>
            </b-col>
          </b-row>
          <b-row>
            <b-container ref="activityList" fluid id="activityList" class="bg-dark">
              <b-row v-if="!activities.length" class="h-100">
                <b-col v-if="loading" class="text-white text-center m-auto">
                  Loading Activities...
                </b-col>
                <b-col v-else class="text-white text-center m-auto">
                  Please use the "+ Add Activity" button to map an activity (harvest, road construction, or water
                  crossing).
                  At least one activity is required to continue.
                </b-col>
              </b-row>
              <ValidationObserver ref="desktopObserver" slim>
                <b-row class="activity-item text-dark p-2 align-items-center" :class="getItemClass(activity.activityId)"
                  v-for="activity in activities" :key="activity.activityId" :id="`act-row-${activity.activityId}`">
                  <b-col cols="3" @click="zoomToActivity(activity)">
                    <b-form-row>
                      <b-col cols="5">
                        {{ activity.activityId }}
                      </b-col>
                      <b-col cols="7">
                        {{ activity.type.name }}
                      </b-col>
                    </b-form-row>
                  </b-col>
                  <b-col cols="5" @click="zoomToActivity(activity)">
                    <b-form-row>
                      <b-col cols="4" class="d-flex align-items-center">
                        {{ activity.size }}
                      </b-col>
                      <b-col cols="4">
                        <template v-if="(currentActivityId === activity.activityId
                        || importActivityIds.indexOf(activity.activityId) !== -1)
                        && activityPermanenceRequired(activity)">
                          <ValidationProvider name="Permanence" rules="required" v-slot="{ errors, classes }" slim>
                            <select id="structurePermanence" name="structurePermanence"
                              v-model="activity.structurePermanence" class="form-control" :class="classes"
                              :aria-describedby="$errorsIdString(errors, 'structurePermanence')">
                              <option :value="null" disabled>Pick one</option>
                              <option :value="false">Temporary</option>
                              <option :value="true">Permanent</option>
                            </select>
                            <InputErrors :errors="errors" errorsIdPrefix="structurePermanence" />
                          </ValidationProvider>
                        </template>
                        <template v-else>
                          {{ getActivityPermanence(activity) }}
                        </template>
                      </b-col>
                      <b-col cols="4">
                        <template v-if="(currentActivityId === activity.activityId
                        || importActivityIds.indexOf(activity.activityId) !== -1)
                        && structureTypeRequired(activity)">
                          <ValidationProvider name="Structure Type" rules="required" v-slot="{ errors, classes }" slim>
                            <select id="structureType" name="structureType" v-model="activity.structureTypeId"
                              class="form-control" :class="classes"
                              :aria-describedby="$errorsIdString(errors, 'structureType')">
                              <option :value="null" disabled>Pick One</option>
                              <option v-for="option in structureTypes" :key="option.id" :value="option.id">
                                {{ option.name }}
                              </option>
                            </select>
                            <InputErrors :errors="errors" errorsIdPrefix="structureType" />
                          </ValidationProvider>
                        </template>
                        <template v-else>
                          {{ getStructureTypeName(activity.structureTypeId) || 'N/A'}}
                        </template>
                      </b-col>
                    </b-form-row>
                  </b-col>
                  <b-col cols="4">
                    <b-row class="justify-content-end align-items-center">
                      <template
                        v-if="currentActivityId === activity.activityId || importActivityIds.indexOf(activity.activityId) !== -1">
                        <b-col v-if="currentActivityId === activity.activityId" cols="7" class="text-danger p-1">
                          {{ currentActivityError }}
                        </b-col>
                        <b-col cols="5">
                          <b-row class="d-flex flex-row justify-content-end">
                            <b-button @click="save(activity)" variant="secondary" pill class="py-1 mr-2"
                              :disabled="drawing || submitting" aria-label="Save Activity">
                              Save
                            </b-button>
                            <b-button variant="danger" pill class="py-1 ml-2"
                              v-b-modal="`confirmation-activity-${activity.activityId}`"
                              @onConfirmed="cancelAddActivity(activity)" aria-label="Cancel Activity">
                              Cancel
                            </b-button>
                            <ConfirmModal :modalId="`confirmation-activity-${activity.activityId}`"
                              :displayName="`Cancel ${activity.type.name} ${activity.activityId}`"
                              prompt="Are you sure you want to cancel the activity?"
                              @onConfirmed="cancelAddActivity(activity)">
                            </ConfirmModal>
                          </b-row>
                        </b-col>
                      </template>
                      <template v-else>
                        <b-button :disabled="mfsAdmin || submitting" variant="danger" pill class="py-1 ml-2"
                          v-b-modal="`deleteConfirmation-activity-${activity.activityId}`"
                          @deleteConfirmed="deleteActivity(activity)" aria-label="Delete Activity">
                          Delete
                        </b-button>
                        <DeleteModal :modalId="`deleteConfirmation-activity-${activity.activityId}`"
                          :deleteDisplayName="`Delete ${activity.type.name} ${activity.activityId}`" deletePrompt="Are you sure you want to permanently delete this mapped
                          activity?" @deleteConfirmed="deleteActivity(activity)">
                        </DeleteModal>
                      </template>
                    </b-row>
                  </b-col>
                </b-row>
              </ValidationObserver>
            </b-container>
          </b-row>
        </div>
      </b-collapse>
    </b-container>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import LookupModules from '@/store/moduleConfig';
import DeleteModal from '@/components/DeleteModal.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import Map from '@/components/Map.vue';
import GeoUtilities from '@/mixins/GeoUtilities';
import Utilities from '@/mixins/Utilities';

export default {
  name: 'MappingForm',
  mixins: [Utilities, GeoUtilities],
  data() {
    return {
      isMapSection: true,
      initialHelp: true,
      harvestWorkflow: false,
      roadConstructionWorkflow: false,
      waterCrossingWorkflow: false,
      drawerOpen: true,
      mapKey: true,
      showWorkflowHelp: true,
      uploadError: '',
      initialHelpControl: {
        title: 'About Map Layers',
        mobileText: `
        While planning harvest areas you can use the layer tool on the right side of the 
        map to toggle reference layers on and off.`,
        desktopText: `
        While planning harvest areas you can use the layer tool on the right side of the 
        map to toggle reference layers on and off.`,
      },
      harvestHelpControl: {
        title: 'How to Map a Harvest Area',
        mobileText: `
        Tap in the map to draw points around the area of the harvest. Close the shape by
        double-tapping. Tap and drag points to adjust. Double tap on a point to remove it. A
        minimum of 3 points is required before saving.`,
        desktopText: `
        Click in the map to draw points around the Harvest area boundary. Double-click to stop
        drawing. A minimum of 3 points is required. To adjust the boundary, click and drag a
        point to a new position. To delete a point, hold the Alt key and left-click the point.
        Click Save in the panel below when finished.`,
      },
      roadHelpControl: {
        title: 'How to Map a Road Construction',
        mobileText: `
        Tap in the map to draw points where the road will be constructed. Finish
        the shape by double-tapping. Tap and drag points to adjust. Double-tap on a point to remove
        it. A minimum of 2 points is required before saving.`,
        desktopText: `
        Click in the map to draw points that mark the path for the Road Construction activity.
        Double-click to stop drawing. A minimum of 2 points is required. To adjust the line,
        click and drag a point to a new position. To delete a point, hold the Alt key and
        left-click the point. Click Save in the panel below when finished.`,
      },
      crossingHelpControl: {
        title: 'How to Map a Water Crossing Point',
        mobileText: `
        Tap in the map to draw a point to mark the location of the water crossing.
        Tap and drag point to move it. You must map the point before saving.`,
        desktopText: `
        Click in the map to a draw point that marks the location of a Water Crossing
        Construction. To move the point, click and drag a point to a new position.
        Click Save in the panel below when finished.`,
      },
      activityErrorDismissed: true,
      activityIdToCancel: 0,
      activityTypeToCancel: '',
      needLupcMessageModal: false,
    };
  },
  components: {
    DeleteModal,
    ConfirmModal,
    Map,
  },
  created() {
    this.$store.dispatch(`${LookupModules.STRUCTURE_TYPES.namespace}/fetchOptions`);
  },
  mounted() {
    // Wait for all child components to be mounted and DOM to be ready
    if (!this.notification.isLUPCTown) this.turnOnOffLupcLayer('off');
  },
  beforeDestroy() {
    // Capture layer visibility before component is destroyed
    if (this.$refs.map) {
      this.$refs.map.captureLayerVisibility();
    }
  },
  computed: {
    ...mapState('Notification', {
      townExtent: state => state.townExtent,
      notification: state => state.notification,
      oldTownGeocodeNum: state => state.notification.oldTownGeocodeNum,
    }),
    ...mapState('Configuration', {
      importMaxShapeCount: state => state.configuration.importMaxShapeCount,
      importMaxVertsPerShape: state => state.configuration.importMaxVertsPerShape,
      importMaxVertsPerFile: state => state.configuration.importMaxVertsPerFile,
      basemapLayers: state => state.configuration.mapConfiguration.basemapLayers,
      referenceLayers: state => state.configuration.mapConfiguration.referenceLayers,
    }),
    ...mapState('Activity', {
      activities: state => state.activities,
      currentActivityId: state => state.currentActivityId,
      importActivityIds: state => state.importActivityIds,
      nextId: state => state.nextId,
      currentActivityError: state => state.currentActivityError,
      drawing: state => state.drawing,
      editing: state => state.editing,
      loading: state => state.loading,
      activityExtent: state => state.activityExtent,
      activityTypes: state => state.activityTypes,
      submitting: state => state.submitting,
    }),
    ...mapState(LookupModules.STRUCTURE_TYPES.namespace, {
      structureTypes: state => state.options,
    }),
    ...mapState(['isExtraSmall']),
    ...mapGetters('Activity', [
      'currentActivity',
      'activityPermanenceRequired',
      'getActivityPermanence',
      'structureTypeRequired',
      'activityPermanenceRequiredByType',
      'structureTypeRequiredByType',
    ]),
    ...mapGetters(LookupModules.STRUCTURE_TYPES.namespace, {
      getStructureTypeName: 'getName',
    }),
    ...mapGetters('User', ['mfsAdmin']),
    workflowActive() {
      return this.harvestWorkflow || this.roadConstructionWorkflow || this.waterCrossingWorkflow;
    },
    attributions() {
      let attr = this.basemapLayers.filter(layer => layer.attribution !== null);
      attr = attr.concat(this.referenceLayers.filter(layer => layer.attribution !== null));
      return attr;
    },
    // Computed property to compare old and new townGeocodeNum values
    isLupcTownChanged() {
      // Get current and stored town values
      const currentTown = this.notification.townGeocodeNum;
      const storedTown = this.notification.oldTownGeocodeNum;
      const lupcModalDisplayedBefore = this.notification.lupcModal;
      console.log('isLupcTownChanged computation:', {
        currentTown,
        storedTown,
        isLUPCTown: this.notification.isLUPCTown,
        bothExist: currentTown !== undefined && storedTown !== undefined,
        hasChanged: currentTown !== storedTown,
        lupcModalBefore: lupcModalDisplayedBefore,
      });

      // Only return true if:
      // 1. This is a LUPC town
      // 2. current value exists (not null/undefined)
      // 3. The values are different
      // 4. From draft and the modal had not been displayed before
      const shouldShow = this.notification.isLUPCTown
        && currentTown !== undefined
        && (currentTown !== storedTown || !lupcModalDisplayedBefore)
        && !(storedTown === undefined && lupcModalDisplayedBefore);
      console.log('shouldShow:', shouldShow);
      // if (!this.notification.isLUPCTown) this.turnOnOffLupcLayer('off');
      return shouldShow;
    },
  },
  watch: {
    isExtraSmall(val) {
      this.drawerOpen = !val;
    },
  },
  methods: {
    async reloadMap() {
      await this.$store.dispatch('Activity/fetchActivities');
      await this.$store.dispatch('Notification/getTownExtent');
      this.mapKey = !this.mapKey;
    },
    openAboutMap() {
      this.$refs.aboutModal.show();
    },
    closeAboutMap() {
      this.$refs.aboutModal.hide();
    },
    openConfirmCancel(activities) {
      const lastActivity = activities[activities.length - 1];
      this.activityIdToCancel = lastActivity.activityId;
      this.activityTypeToCancel = lastActivity.type.name;
      this.$refs.cancelConfirmModal.show();
    },
    closeConfirmCancel(response = null) {
      this.$refs.cancelConfirmModal.hide();
      if (response) { this.cancelAddActivity(this.activityIdToCancel); }
      this.activityIdToCancel = 0;
    },
    openAddActivity() {
      if (this.$refs.addActivityModal) {
        this.$refs.addActivityModal.show();
      }
    },
    closeAddActivity() {
      if (this.$refs.addActivityModal) {
        this.$refs.addActivityModal.hide();
      }
    },
    openAddActivityMode() {
      if (this.$refs.selectModeModal) {
        this.$refs.selectModeModal.show();
      }
    },
    closeAddActivityMode() {
      if (this.$refs.selectModeModal) {
        this.$refs.selectModeModal.hide();
      }
    },
    openUploadActivity() {
      if (this.$refs.uploadActivityModal) {
        this.$refs.uploadActivityModal.show();
      }
    },
    closeUploadActivity() {
      if (this.$refs.uploadActivityModal) {
        this.$refs.uploadActivityModal.hide();
      }
    },
    openUploadActivitySuccess() {
      this.$refs.uploadActivitySuccessModal.show();
    },
    closeUploadActivitySuccess() {
      this.$refs.uploadActivitySuccessModal.hide();
    },
    openUploadActivityError(message) {
      this.uploadError = message;
      this.$refs.uploadActivityErrorModal.show();
    },
    closeUploadActivityError() {
      this.uploadError = '';
      this.$refs.uploadActivityErrorModal.hide();
    },
    openActivityCreated() {
      this.$refs.activityCreatedModal.show();
    },
    closeActivityCreated() {
      this.$refs.activityCreatedModal.hide();
    },
    closeOnLupcMessageModal() {
      this.updateOldTownGeocodeNum();
      this.$refs.lupcMessageModal.hide();
      // Turn on LUPC zones layer
      this.turnOnOffLupcLayer('on');
      const updateInfo = { notificationId: this.notification.id, status: 1 };
      this.$store.dispatch('Notification/updateLupcModalStatus', updateInfo);
    },
    closeLupcMessageModal() {
      this.updateOldTownGeocodeNum();
      this.$refs.lupcMessageModal.hide();
      // Try to save draft by finding NotificationForm in component tree
      const updateInfo = { notificationId: this.notification.id, status: 1 };
      this.$store.dispatch('Notification/updateLupcModalStatus', updateInfo);
    },
    callSaveDraft() {
      // Look for NotificationForm in the component tree
      let parent = this.$parent;
      while (parent) {
        if (parent.$options.name === 'NotificationForm' && parent.saveDraft) {
          parent.saveDraft();
          return;
        }
        parent = parent.$parent;
      }

      // If we can't find NotificationForm, try emitting event
      this.$emit('save-draft');
    },
    closeBackLupcMessageModal() {
      this.updateOldTownGeocodeNum();
      this.$refs.lupcMessageModal.hide();
      const updateInfo = { notificationId: this.notification.id, status: 1 };
      this.$store.dispatch('Notification/updateLupcModalStatus', updateInfo);
      // Emit event to parent to navigate back to Overview tab
      this.$emit('navigate-to-overview');
    },
    updateOldTownGeocodeNum() {
      // Update the stored town value so future changes will be detected
      this.$store.commit('Notification/updateNotification', {
        oldTownGeocodeNum: this.notification.townGeocodeNum,
      });
    },
    turnOnOffLupcLayer(requestedStatus) {
      // Access the map component and turn on LUPC layer
      if (this.$refs.map && this.$refs.map.map) {
        const _map = this.$refs.map.map;
        // Find the reference layers group
        const layerGroups = _map.getLayers().getArray();
        const referenceLayerGroup = layerGroups.find(group => group.get('title') === 'Reference');

        if (referenceLayerGroup) {
          const referenceLayers = referenceLayerGroup.getLayers().getArray();
          // Look for LUPC layer (could be named 'LUPC', 'LUPC Zones', 'Land Use Planning Commission', etc.)
          const lupcLayer = referenceLayers.find((layer) => {
            const title = layer.get('title');
            return title && (title.toLowerCase().includes('lupc zones'));
          });
          if (lupcLayer) {
            // Check the checkbox for LUPC zones layer
            const layerElement = document.getElementById('LUPC Zones');
            if (layerElement) {
              const lupcCheckbox = layerElement.querySelector('input');
              if (lupcCheckbox) {
                if (requestedStatus === 'on') {
                  lupcCheckbox.checked = true;

                  // Try multiple event types to ensure the change is detected
                  lupcCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
                  lupcCheckbox.dispatchEvent(new Event('input', { bubbles: true }));
                  lupcCheckbox.dispatchEvent(new Event('click', { bubbles: true }));

                  // Also try setting the layer visibility directly
                  if (lupcLayer) {
                    lupcLayer.setVisible(true);
                  }

                  const layerControlCollapseBlock = document.getElementById('layerControlCollapse');
                  // Expand layer control collapse if it's not already showing
                  if (layerControlCollapseBlock && !layerControlCollapseBlock.className.includes('show')) {
                    layerControlCollapseBlock.classList.add('show');
                    if (this.isExtraSmall) { // in case of mobile
                      const immediateParent = layerControlCollapseBlock.parentElement;
                      const grandparentElement = immediateParent.parentElement;
                      grandparentElement.classList.add('show');
                      layerControlCollapseBlock.classList.add('mobile-layer-control');
                      const aboutMapBtnElement = document.getElementById('aboutMapBtn');
                      aboutMapBtnElement.classList.add('low-z-index');
                    }
                  }
                } else if (requestedStatus === 'off') {
                  lupcCheckbox.checked = false;

                  // Try multiple event types to ensure the change is detected
                  lupcCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
                  lupcCheckbox.dispatchEvent(new Event('input', { bubbles: true }));
                  lupcCheckbox.dispatchEvent(new Event('click', { bubbles: true }));
                  const aboutMapBtnElement = document.getElementById('aboutMapBtn');
                  aboutMapBtnElement.classList.remove('low-z-index');

                  // Also try setting the layer visibility directly
                  if (lupcLayer) {
                    lupcLayer.setVisible(false);
                  }
                }
              }
            }
          }
        }
      }
    },
    zoomToActivity(act) {
      this.$refs.map.zoomToFeature(act.type.name, act.activityId);
    },
    openAddActivitySelector() {
      if (!this.isExtraSmall) {
        this.openAddActivityMode();
      } else {
        this.openAddActivity();
      }
    },
    activityMapClick(act) {
      // If the drawer is not open wait for a beat or the scrolling won't work
      if (!this.drawerOpen) {
        this.drawerOpen = true;
        setTimeout(() => this.scrollToActivity(act), 250);
      } else {
        this.scrollToActivity(act);
      }
    },
    scrollToActivity(act) {
      this.$scrollTo(document.querySelector(`#act-row-${act}`), {
        container: document.querySelector('#activityList') || document.querySelector('.activity-list-sm'),
      });
    },
    addActivity(type) {
      if (this.mfsAdmin || this.drawing || this.editing) return;
      this.closeAddActivity();
      this.$store.dispatch('Activity/addActivity', type)
        .then(() => {
          switch (type) {
            case this.activityTypes.harvest:
              this.beginHarvestWorkflow();
              break;

            case this.activityTypes.roadConstruction:
              this.beginRoadConstructionWorkflow();
              break;

            case this.activityTypes.waterCrossing:
              this.beginWaterCrossingWorkflow();
              break;

            default:
              break;
          }
        });
    },
    cancelAddActivity(activity = null) {
      const act = activity || this.currentActivity;
      this.endWorkflow();
      this.cancelDrawing(act.activityId);
    },
    getItemClass(id) {
      return this.currentActivityId === id ? 'border border-info border-3' : 'border-bottom border-dark';
    },
    scrollToDrawerBottom() {
      this.$refs.activityList.scrollTop = this.$refs.activityList.scrollHeight;
    },
    resizeMap() {
      if (this.$refs.map) {
        this.$refs.map.resizeMap();
      }
    },
    async save(activity) {
      if (this.mfsAdmin) {
        return;
      }
      if (this.$refs.desktopObserver && !await this.$refs.desktopObserver.validate()) {
        this.scrollToDrawerBottom();
        return;
      }
      this.$refs.map.saveActivity(activity ? activity.activityId : this.currentActivityId).then(() => {
        this.endWorkflow();
        this.openActivityCreated();
      }).finally(() => { this.activityErrorDismissed = false; });
    },
    submitWrapper() {
      // Capture layer visibility before submitting
      if (this.$refs.map) {
        this.$refs.map.captureLayerVisibility();
      }
      return !this.mfsAdmin && !this.editing && !this.drawing;
    },
    cancelDrawing(activityId) {
      if (this.mfsAdmin) {
        return;
      }
      this.$refs.map.cancelDrawing(activityId !== undefined ? activityId : this.currentActivityId);
      if (this.currentActivityId !== undefined) {
        this.$store.dispatch('Activity/removeImportActivity', activityId);
      } else {
        this.$store.dispatch('Activity/removeCurrentActivity');
      }
    },
    deleteActivity(activity) {
      if (this.mfsAdmin) {
        return;
      }
      this.$store.dispatch('Activity/delete', activity);
    },
    hasCalculatedSize(activity) {
      return activity.size !== null
        && typeof activity.size !== 'undefined'
        && activity.size !== 'N/A';
    },
    hasStructurePermanence(activity) {
      return activity.structurePermanence !== null
        && typeof activity.structurePermanence !== 'undefined';
    },
    hasStructureTypeId(activity) {
      return activity.structureTypeId !== null
        && typeof activity.structureTypeId !== 'undefined';
    },
    permanenceToString(activity) {
      if (!this.hasStructurePermanence(activity)) {
        return 'N/A';
      }
      return activity.structurePermanence ? 'Permanent' : 'Temporary';
    },
    hideWorkflowHelp() {
      this.showWorkflowHelp = false;
    },
    beginHarvestWorkflow() {
      if (this.isExtraSmall) this.drawerOpen = false;
      this.roadConstructionWorkflow = false;
      this.waterCrossingWorkflow = false;
      this.harvestWorkflow = true;
      this.showWorkflowHelp = true;
      this.$nextTick(() => {
        this.$refs.map.drawHarvest(this.currentActivityId);
      });
    },
    beginRoadConstructionWorkflow() {
      this.waterCrossingWorkflow = false;
      this.harvestWorkflow = false;
      this.roadConstructionWorkflow = true;
      this.$nextTick(() => {
        this.$refs.roadDetailsModal.show();
      });
    },
    continueRoadConstructionWorkflow() {
      this.$refs.roadDetailsModal.hide();
      if (this.isExtraSmall) this.drawerOpen = false;
      this.showWorkflowHelp = true;
      this.$nextTick(() => {
        this.$refs.map.drawRoadConstruction(this.currentActivityId);
      });
    },
    beginWaterCrossingWorkflow() {
      this.closeAddActivity();
      this.harvestWorkflow = false;
      this.roadConstructionWorkflow = false;
      this.waterCrossingWorkflow = true;
      this.$nextTick(() => {
        this.$refs.waterCrossingDetailsModal.show();
      });
    },
    continueWaterCrossingWorkflow() {
      this.$refs.waterCrossingDetailsModal.hide();
      if (this.isExtraSmall) this.drawerOpen = false;
      this.showWorkflowHelp = true;
      this.$nextTick(() => {
        this.$refs.map.drawWaterCrossing(this.currentActivityId);
      });
    },
    endWorkflow() {
      if (this.$refs.roadDetailsModal) {
        this.$refs.roadDetailsModal.hide();
      }
      if (this.$refs.waterCrossingDetailsModal) {
        this.$refs.waterCrossingDetailsModal.hide();
      }
      this.drawerOpen = true;
      this.harvestWorkflow = false;
      this.roadConstructionWorkflow = false;
      this.waterCrossingWorkflow = false;
      this.showWorkflowHelp = true;
    },
    getInitHelpTitle() {
      return this.initialHelpControl.title;
    },
    getInitHelpText() {
      return this.isExtraSmall
        ? this.initialHelpControl.mobileText
        : this.initialHelpControl.desktopText;
    },
    getWorkflowTitle() {
      if (this.harvestWorkflow) {
        return this.harvestHelpControl.title;
      }
      if (this.roadConstructionWorkflow) {
        return this.roadHelpControl.title;
      }
      if (this.waterCrossingWorkflow) {
        return this.crossingHelpControl.title;
      }
      return '';
    },
    getWorkflowText() {
      if (this.harvestWorkflow) {
        return this.isExtraSmall
          ? this.harvestHelpControl.mobileText
          : this.harvestHelpControl.desktopText;
      }
      if (this.roadConstructionWorkflow) {
        return this.isExtraSmall
          ? this.roadHelpControl.mobileText
          : this.roadHelpControl.desktopText;
      }
      if (this.waterCrossingWorkflow) {
        return this.isExtraSmall
          ? this.crossingHelpControl.mobileText
          : this.crossingHelpControl.desktopText;
      }
      return '';
    },
    beginDrawWorkflow() {
      this.closeAddActivityMode();
      this.$nextTick(() => {
        this.openAddActivity();
      });
    },
    beginImportWorkflow() {
      this.closeAddActivityMode();
      this.$nextTick(() => {
        this.openUploadActivity();
      });
    },
    async loadShapeFile(event) {
      this.closeUploadActivity();

      let validation = null;
      let collection = null;

      try {
        const zipFile = event.target.files[0];
        if (this.hasExtension(zipFile.name, '.zip') === false) {
          this.openUploadActivityError('The file you are attempting to upload is not a .zip file. Please upload a .zip file that includes at least .shp, .dbf, .prj, .shx file types.');
          return;
        }

        collection = await this.shapefileToGeojson(zipFile);
        validation = this.validateFeatures(collection);

        if (validation.valid) {
          const startId = this.nextId;
          const defaultstructurePermanence = this.activityPermanenceRequiredByType(validation.activityType) ? false : null;
          const defaultstructureTypeId = this.structureTypeRequiredByType(validation.activityType) ? this.structureTypes[0].id : null;

          this.$store.dispatch('Activity/importFeatures', {
            type: validation.activityType,
            features: collection.features,
            defaultstructurePermanence,
            defaultstructureTypeId,
          }).then(() => {
            this.closeUploadActivity();
            this.$refs.map.loadFeatures(startId, collection.features, validation.activityType);
            this.openUploadActivitySuccess();
          });
        } else {
          this.openUploadActivityError(validation.message);
        }
      } catch (error) {
        this.openUploadActivityError('An upload error occurred. Please try uploading the .zip file again.');
      }
    },
    validateFeatures(collection) {
      const { features } = collection;

      if (!features || features.length === 0) {
        return {
          valid: false,
          message: 'The file you have uploaded does not contain any features. Please upload a .zip file that includes at least .shp, .dbf, .prj, .shx file types.',
        };
      }

      if (features.length > this.importMaxShapeCount) {
        return {
          valid: false,
          message: `There are more than ${this.localeString(this.importMaxShapeCount)} shapes present in this shapefile. Please upload another shapefile that contains ${this.localeString(this.importMaxShapeCount)} or less.`,
        };
      }

      const [maxVertsFileCount, maxVertsShapeCount] = this.maxPointCount(collection);

      if (maxVertsShapeCount > this.importMaxVertsPerShape) {
        return {
          valid: false,
          message: `A shape cannot exceed ${this.localeString(this.importMaxVertsPerShape)} vertices. Please simplify the shape before uploading if it exceeds ${this.localeString(this.importMaxVertsPerShape)} vertices.`,
        };
      }

      if (maxVertsFileCount > this.importMaxVertsPerFile) {
        return {
          valid: false,
          message: `There are more than ${this.localeString(this.importMaxVertsPerFile)} vertices present in this shapefile. Please upload another shapefile that contains ${this.localeString(this.importMaxVertsPerFile)} or less.`,
        };
      }

      // determine the activity type based on the feature types
      const featureTypes = {
        Polygon: {
          count: 0,
          type: this.activityTypes.harvest,
        },
        LineString: {
          count: 0,
          type: this.activityTypes.roadConstruction,
        },
        Point: {
          count: 0,
          type: this.activityTypes.waterCrossing,
        },
      };

      features.forEach((feature) => {
        if (feature.geometry.type in featureTypes) {
          featureTypes[feature.geometry.type].count += 1;
        }
      });

      const key = Object.keys(featureTypes).find(key => featureTypes[key].count > 0);

      return {
        valid: true,
        message: '',
        activityType: featureTypes[key].type,
      };
    },
  },
};
</script>
<style>
  .lupc-modal-title{
    text-align: center;
    color: #01893F;
  }
  .lupc-modal-text {
    font-size: 13px;
    color: #696969ff;
  }
  .btn-wrapper {
    align-items: center;
    > button {
      font-size: 13px;
      width: 220px;
    }
  }
  .back-to-overview-wrapper {
    margin-top: 14px;
    margin-bottom: -18px;
    text-align: center;
    font-size: 13px;
    text-decoration: underline;
    display: flex;
    justify-content: center;
  }
  .back-to-overview {
    cursor: pointer;
  }
  .footer-hide {
    display: none;
  }
  .mobile-layer-control {
    font-size: 1rem;
    z-index: 2 !important;
  }
  .low-z-index {
    z-index: -1 !important;
  }
  .lupc-modal-btn {
    width: 252px !important;
  }
</style>
